/**
 * Progress Nav Manager for KeenSlider
 * Handles active state + progress-fill animation for custom dot navigation.
 */
class ProgressNavManager {
	constructor(sliderInstance, parentContainer, sliderId) {
		this.slider = sliderInstance;
		this.parentContainer = parentContainer;
		this.sliderId = sliderId;
		this.navElement = null;
		this.progressDots = [];
		this.boundHandleDotClick = null;
		this.onDotClick = null;
	}

	/**
	 * Initialize progress nav controls.
	 *
	 * @param {Function} onDotClick Callback fired with the target slide index.
	 * @return {boolean} True when dots were found and initialized.
	 */
	init(onDotClick) {
		if (!this.parentContainer || !this.slider) return false;

		this.navElement = this.findNavElement();
		if (!this.navElement) return false;

		this.progressDots = Array.from(
			this.navElement.querySelectorAll('.progress-slider__dot'),
		);

		if (!this.progressDots.length) {
			this.generateDots();
			this.progressDots = Array.from(
				this.navElement.querySelectorAll('.progress-slider__dot'),
			);
		}

		if (!this.progressDots.length) return false;

		this.onDotClick = typeof onDotClick === 'function' ? onDotClick : null;
		this.boundHandleDotClick = this.handleDotClick.bind(this);
		this.progressDots.forEach((dot) => {
			dot.addEventListener('click', this.boundHandleDotClick);
		});

		this.updateActive(this.slider.track.details.rel);
		return true;
	}

	/**
	 * Find a nav element linked to this slider.
	 *
	 * @return {HTMLElement|null} Matching progress nav element, if present.
	 */
	findNavElement() {
		if (!this.parentContainer) return null;

		if (this.sliderId) {
			return this.parentContainer.querySelector(
				`.progress-slider__nav[data-slider-id="${this.sliderId}"]`,
			);
		}

		return this.parentContainer.querySelector('.progress-slider__nav');
	}

	/**
	 * Generate dot buttons to match current slide count.
	 */
	generateDots() {
		if (!this.navElement || !this.slider || !this.slider.track) return;

		const details = this.slider.track.details;
		const slideCount =
			details && Array.isArray(details.slides) ? details.slides.length : 0;
		if (!slideCount) return;

		this.navElement.innerHTML = '';

		for (let index = 0; index < slideCount; index++) {
			const dot = document.createElement('button');
			dot.className = 'progress-slider__dot';
			dot.type = 'button';
			dot.dataset.slide = `${index}`;
			dot.setAttribute('aria-label', `Go to slide ${index + 1}`);

			const dotBackground = document.createElement('span');
			dotBackground.className = 'progress-slider__dot-bg';
			dot.appendChild(dotBackground);

			const dotFill = document.createElement('span');
			dotFill.className = 'progress-slider__fill';
			dot.appendChild(dotFill);

			this.navElement.appendChild(dot);
		}
	}

	/**
	 * Handle click on a progress dot.
	 *
	 * @param {MouseEvent} event Click event from dot button.
	 */
	handleDotClick(event) {
		if (!event.currentTarget || !this.onDotClick) return;

		const index = parseInt(event.currentTarget.dataset.slide, 10);
		if (Number.isNaN(index)) return;

		this.onDotClick(index);
	}

	/**
	 * Set active dot and reset non-active fills.
	 *
	 * @param {number} activeIndex Active slide index.
	 */
	updateActive(activeIndex) {
		if (!this.progressDots.length) return;

		this.progressDots.forEach((dot, index) => {
			const fill = dot.querySelector('.progress-slider__fill');
			const isActive = index === activeIndex;

			dot.classList.toggle('is-active', isActive);
			dot.setAttribute('aria-current', isActive ? 'true' : 'false');

			if (fill && !isActive) {
				fill.style.transition = 'none';
				fill.style.transform = 'scaleX(0)';
			}
		});
	}

	/**
	 * Animate the active dot's fill based on autoplay duration.
	 *
	 * @param {number}           duration    Animation duration in milliseconds.
	 * @param {number|undefined} activeIndex Dot index to animate; falls back to slider's current rel index.
	 */
	animateActiveFill(duration, activeIndex) {
		if (!this.progressDots.length) return;

		const fallback = this.slider ? this.slider.track.details.rel : 0;
		const index = activeIndex ?? fallback;
		const activeDot = this.progressDots[index];
		if (!activeDot) return;

		const fill = activeDot.querySelector('.progress-slider__fill');
		if (!fill) return;

		fill.style.transition = 'none';
		fill.style.transform = 'scaleX(0)';
		// Force a synchronous reflow so scaleX(0) is committed as the painted starting
		// state before the transition begins. A single requestAnimationFrame is not
		// enough — it fires before paint, so both the reset and the scaleX(1) target
		// land in the same frame and no animation fires (most visible on first load
		// or when the fill was already at scaleX(0)).
		// eslint-disable-next-line no-unused-expressions
		fill.offsetWidth;
		fill.style.transition = `transform ${duration}ms linear`;
		fill.style.transform = 'scaleX(1)';
	}

	/**
	 * Pause active fill transitions (used when autoplay pauses/stops).
	 */
	pauseFillTransition() {
		if (!this.progressDots.length) return;

		this.progressDots.forEach((dot) => {
			const fill = dot.querySelector('.progress-slider__fill');
			if (fill) {
				fill.style.transition = 'none';
			}
		});
	}

	/**
	 * Remove listeners and references.
	 */
	destroy() {
		if (this.boundHandleDotClick) {
			this.progressDots.forEach((dot) => {
				dot.removeEventListener('click', this.boundHandleDotClick);
			});
		}

		this.boundHandleDotClick = null;
		this.onDotClick = null;
		this.navElement = null;
		this.progressDots = [];
	}
}

window.ProgressNavManager = ProgressNavManager;
